import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { getFilteredTableData } from "../../../../../shared/utils/tableUtils"
import { Grid, MenuItem, Stack } from "@mui/material"
import { DateTime } from "luxon"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { AccountingQuery, FetchAccountingMeetingServiceResponse, fetchAccountingMeetingService } from "../accountingApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faCircleEuro, faFileArrowDown, faFileCsv, faPrint, faReceipt } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import OrderStatusBadge from "../../../../../shared/components/badges/OrderStatusBadge"
import { LocalizedStrict, LocalizedEnum } from "../../../../localization/components/AppLocalized"
import { Receipt } from "../../../../receipt/pages/ReceiptDialog"
import { useExportOrdersToCsv } from "./ExportFunctionMeetingServiceOrder"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../context/contextSelectors"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../shared/components/table/TableSummaryBox"
import { OrderStatus } from "../../../controlKitchenTypes"
import { MeetingServiceOrder, ReferenceType } from "../accountingTypes"
import {
    defaultMeetingServiceFilterOptionsList,
    MeetingServiceFilterProps,
    FiltersDrawerMeetingService,
} from "../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import useActiveFiltersCount from "../../../../supplier/reporting/accounting/meetingService/hooks/useActiveFiltersCount"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { StandardTabsRowActions } from "../../../../../shared/components/tabs/StandardTabsRowActions"
import { ExpandableSearch } from "../../../../../shared/components/filters/ExpandableSearch"
import { ORDERS_SEARCH_MIN_LENGTH } from "../../../../../shared/components/filters/constants/searchConst"
import {
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../shared/components/table/FloatingActionBar"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardCustomerSelect } from "../../../../../shared/components/filters/components/StandardCustomerSelect"
import { UserMeetingsReceipt } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { AccountingOrderMenu } from "./AccountingOrderMenu"
import { makeStyles } from "tss-react/mui"
import {
    StandardDateRangeFilter,
    type DtDateRange,
} from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useCreditNoteDialog } from "../../../../../shared/components/dialogs/creditNoteDialog/useCreditNoteDialog"
import { getOrderPerson } from "../../../../../shared/utils/getOrderPerson"
import { OrderChatIcon } from "../../../../../shared/components/orderChat/OrderChatIcon"
import { OrderChatDrawer } from "../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../shared/components/orderChat/orderChatTypes"
import { MeetingServiceOrderDrawer } from "../../../../../shared/components/meetingServiceOrderDrawer/MeetingServiceOrderDrawer"
import { isOrderRowControlClick } from "../../../../../shared/components/meetingServiceOrderDrawer/orderDrawerRow"
import { DeliveryType } from "../../../merchant/production/meetingService/meetingServiceTypes"
import {
    filterMeetingServiceOrders,
    type MeetingServiceClientSideFilters,
} from "../../../merchant/production/meetingService/meetingServiceLib"

type MeetingServiceAccountingTabProps = Readonly<{}>

export type FilterOrdersProps = Readonly<{
    deliveryDate: {
        startDate: null | DateTime
        endDate: null | DateTime
    }
    orderDetails: {
        customerItem?: {
            customerId: string | undefined
            groupId: string | undefined
        }
        orderNumber: null | number
    }
}>
export const defaultFilterOrdersOptionsList = {
    orderDetails: {
        orderNumber: null,
    },
    deliveryDate: {
        startDate: null,
        endDate: null,
    },
}

const useStyles = makeStyles()((theme) => ({
    table: {
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    wrapIconText: {
        display: "flex",
        gap: "2px",
        alignItems: "center",
    },
    inlineIconWrapper: {
        display: "inline-block",
        marginLeft: "1rem",
        whiteSpace: "nowrap",
    },
    inlineIcon: {
        verticalAlign: "middle",
        color: theme.palette.tailwind.slate[700],
    },
    inlineIconText: {
        marginLeft: ".25rem",
    },
}))

const selectOrders = (filters: MeetingServiceClientSideFilters) => (data: FetchAccountingMeetingServiceResponse) =>
    filterMeetingServiceOrders(data?.orders || [], filters)

export function MeetingServiceAccountingTab({}: MeetingServiceAccountingTabProps) {
    const { merchantId } = useMerchant()
    const queryClient = useQueryClient()
    const userId = useAppSelector(selectEffectiveUserId)
    const { l10n } = useAppLocalization()
    const { classes, cx } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()

    const [receipt, setReceipt] = useState<Receipt>()
    const [receiptOrders, setReceiptOrders] = useState<MeetingServiceOrder[]>()
    const [chatDialogOrder, setChatDialogOrder] = useState<{ id: number; title: string } | null>(null)
    const [drawerOrder, setDrawerOrder] = useState<MeetingServiceOrder | undefined>(undefined)
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [search, setSearch] = useState("")
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<MeetingServiceFilterProps>({
        ...defaultMeetingServiceFilterOptionsList,
        merchantId: merchantId.toString(),
    })

    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })

    const { activeCount } = useActiveFiltersCount(filtersList)

    // a search or an order id is for an order whose date is unknown, so the period must not narrow it
    const searchQuery = search.length >= ORDERS_SEARCH_MIN_LENGTH ? search : undefined
    const isSearchingAllDates = Boolean(filtersList.orderId) || Boolean(searchQuery)
    const selectFilteredOrders = useMemo(() => selectOrders(filtersList), [filtersList])

    const meetingServiceOrdersResult = useApiQuery(fetchAccountingMeetingService, {
        queryName: AccountingQuery.ACCOUNTING_ORDERS,
        dependencies: {
            ...filtersList,
            merchantId,
            search: searchQuery,
            startBookedDate: isSearchingAllDates ? undefined : dateState.startDate?.toISODate(),
            endBookedDate: isSearchingAllDates ? undefined : dateState.endDate?.toISODate(),
            startDeliveryDate: filtersList.startDeliveryDate?.toISODate(),
            endDeliveryDate: filtersList.endDeliveryDate?.toISODate(),
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        },
        select: selectFilteredOrders,
    })
    const { creditNoteDialog, openCreditNoteDialog } = useCreditNoteDialog()

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceOrdersResult)

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-number"),
                accessorKey: "id",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("accounting-orders-meeting-service-table-booked-date"),
                    accessorKey: "accountingDetails.bookedTime",
                },
                (row) => row.accountingDetails.bookedTime,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("accounting-orders-meeting-service-table-delivery-date"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-delivery-location-column"),
                accessorKey: "deliveryDetails",
                size: COLUMN_SIZE.L,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
                Cell: ({ row }) => {
                    const { id, meetingDetails, deliveryDetails, organizers, messages, paymentDetails } = row.original
                    const deliveryTypeValue = deliveryDetails.type
                    const { responsible } = getOrderPerson(organizers)

                    return (
                        <StandardTwoLinesCell
                            title={
                                <Stack direction="row" alignItems="center" gap="1rem" component="span">
                                    <span>
                                        {meetingDetails.meetingName}
                                        {!!deliveryDetails.sittings && (
                                            <span className={cx(classes.wrapIconText, classes.inlineIconWrapper)}>
                                                <FontAwesomeIcon icon={faUser} className={classes.inlineIcon} />
                                                <span className={classes.inlineIconText}>
                                                    {deliveryDetails.sittings}
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                    <OrderChatIcon
                                        messages={messages}
                                        onClick={() => setChatDialogOrder({ id, title: meetingDetails.meetingName })}
                                    />
                                    {paymentDetails?.isContantPayment && (
                                        <FontAwesomeIcon icon={faCircleEuro} className={classes.inlineIcon} />
                                    )}
                                </Stack>
                            }
                            subtitle1={
                                deliveryTypeValue === DeliveryType.DELIVERY ? (
                                    <LocalizedStrict
                                        id="meeting-service-table-meeting"
                                        vars={{ locationName: deliveryDetails.location.name }}
                                    >
                                        <span>{`Delivery: ${deliveryDetails.location.name}`}</span>
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedEnum base="delivery-type-filter" enumValue={deliveryDetails.type}>
                                        <span>{deliveryTypeValue}</span>
                                    </LocalizedEnum>
                                )
                            }
                            subtitle2={responsible?.name}
                        />
                    )
                },
            }),
            columnFactory.idColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-ref"),
                accessorFn: (row) => {
                    const value = row.accountingDetails?.references?.find(
                        (r) => r.referenceType === ReferenceType.ORIGINAL_ORDER,
                    )

                    return value?.orderId
                },
            }),
            columnFactory.statusColumn({
                header: l10n.getString("accounting-orders-meeting-service-table-status"),
                accessorKey: "orderStatus",
                Cell: ({ row }) => {
                    return <OrderStatusBadge placement="table" status={row.original.orderStatus as OrderStatus | ""} />
                },
                filterSelectOptions: [
                    {
                        value: "CONFIRMED",
                        text: l10n.getString("production-order-status-confirmed"),
                    },
                    {
                        value: "RECEIVED",
                        text: l10n.getString("production-order-status-received"),
                    },
                    {
                        value: "CANCELLED",
                        text: l10n.getString("production-order-status-cancelled"),
                    },
                    {
                        value: "READY",
                        text: l10n.getString("production-order-status-ready"),
                    },
                ],
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("accounting-orders-meeting-service-table-amount"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    function showReceipt(row: MeetingServiceOrder) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const exportOrder = useExportOrdersToCsv({
        data: meetingServiceOrdersResult.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
        includeVatInLabels: true,
    })

    const exportSelectedOrders = useExportOrdersToCsv({
        data: selectedRows,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
        includeVatInLabels: true,
    })

    const handleClearSelected = useCallback(() => {
        setSelectedRows(undefined)
        setRowSelection({})
    }, [])

    // row selection is index-based, so it must not outlive the list it was made on
    useEffect(() => {
        handleClearSelected()
    }, [meetingServiceOrdersResult.data, handleClearSelected])

    return (
        <>
            <StandardTabsRowActions>
                <LocalizedStrict
                    id="accounting-orders-meeting-service-search-field"
                    attrs={{ label: true, placeholder: true }}
                >
                    <ExpandableSearch
                        value={search}
                        onChange={setSearch}
                        label="Search order"
                        placeholder="Type order id, meeting name or host."
                        expandedWidth="25.25rem"
                        bordered
                    />
                </LocalizedStrict>
                <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} bordered />
                <StandardExportButton onClick={() => exportOrder()} bordered />
            </StandardTabsRowActions>
            <Grid container direction="column" wrap="nowrap" className={classes.table}>
                <ApiQueryTable
                    layoutMode="grid"
                    muiTablePaperProps={{ className: gridClasses.pinned }}
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    muiTableBodyRowProps={({ row }) => ({
                        onClick: (event) => {
                            if (isOrderRowControlClick(event)) return
                            setDrawerOrder(row.original)
                        },
                        sx: { cursor: "pointer" },
                    })}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="order-chat-menu-item-merchant">
                            <MenuItem
                                onClick={() => {
                                    setChatDialogOrder({
                                        id: row.original.id,
                                        title: row.original.meetingDetails.meetingName,
                                    })
                                    closeMenu()
                                }}
                            >
                                Chat with customer
                            </MenuItem>
                        </LocalizedStrict>,
                        <AccountingOrderMenu
                            setShowReceiptModal={showReceipt}
                            openCreditNoteDialog={openCreditNoteDialog}
                            order={row.original}
                            handleMenuClose={closeMenu}
                        />,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table} showGlobalFilterButton={false} />
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                            <StandardCustomerSelect
                                setCustomerId={(customerId) =>
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        customerId,
                                    }))
                                }
                                setCustomerGroupId={(userGroupId) =>
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        userGroupId,
                                    }))
                                }
                                customerId={filtersList.customerId}
                                customerGroupId={filtersList.userGroupId}
                            />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={meetingServiceOrdersResult.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={meetingServiceOrdersResult.isLoading}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                    state={{
                        rowSelection,
                    }}
                />
            </Grid>
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict id="meeting-service-table-floating-action-bar-print" attrs={{ title: true }}>
                        <ActionBarMenu icon={faPrint} title="Print">
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-receipt"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faReceipt}
                                    title="Receipt"
                                    onClick={() => setReceiptOrders(selectedRows)}
                                />
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>

                    <LocalizedStrict id="floating-action-button-export" attrs={{ title: true }}>
                        <ActionBarMenu icon={faFileArrowDown} title="Export">
                            <LocalizedStrict id="export-button-export-as-csv-title" attrs={{ title: true }}>
                                <ActionBarMenuItem
                                    icon={faFileCsv}
                                    title="Export as CSV"
                                    onClick={() => exportSelectedOrders()}
                                />
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            {receipt && (
                <UserMeetingsReceipt
                    open={Boolean(receipt)}
                    onClose={() => setReceipt(undefined)}
                    id={receipt.orderId}
                    meetings={getFilteredTableData(tableInstanceRef)}
                />
            )}

            {receiptOrders && (
                <UserMeetingsReceipt
                    open
                    singleMode={false}
                    meetings={receiptOrders}
                    onClose={() => setReceiptOrders(undefined)}
                />
            )}
            {creditNoteDialog}

            <FiltersDrawerMeetingService
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                tab="accounting-meeting-service"
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
                disabledFilters={{
                    disableMerchantShops: true,
                    disableServicePayment: true,
                }}
            />

            {chatDialogOrder !== null && (
                <OrderChatDrawer
                    open
                    onClose={() => setChatDialogOrder(null)}
                    orderId={chatDialogOrder.id}
                    orderTitle={chatDialogOrder.title}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                    onMessageSent={() =>
                        queryClient.invalidateQueries({
                            queryKey: [AccountingQuery.ACCOUNTING_ORDERS],
                        })
                    }
                    onSubtitleClick={() => {
                        const order = getFilteredTableData(tableInstanceRef).find((o) => o.id === chatDialogOrder.id)
                        if (order) {
                            showReceipt(order)
                        }
                    }}
                />
            )}

            {drawerOrder && (
                <MeetingServiceOrderDrawer
                    open
                    order={drawerOrder}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                    onClose={() => setDrawerOrder(undefined)}
                />
            )}
        </>
    )
}
